import { useCallback, useRef } from 'react';
import { simulatableCreatePerpMarketOrderIxs } from '../trade/perp/useOpenPerpMarketOrder';
import useCurrentUserAccount from '../useCurrentUserAccount';
import { notify } from 'src/utils/notifications';
import useDriftClient from '../useDriftClient';
import { VelocityClient, PositionDirection } from '@velocity-exchange/sdk';
import { ORDER_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/orderPrep';
import { syncGetCurrentSettings } from 'src/environmentVariables/EnvironmentVariables';
import { PerpTradeFormOutputProps } from 'src/stores/DriftStore/actions/actionHelpers/types';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';
import { useGetTxParams } from '../driftActions/transactions/useGetTxParams';
import {
	createCancelOrdersIx,
	createSettlePnlIx,
	MarketId,
	OpenPosition,
	UIMarket,
} from '@velocity-exchange/common';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { getUserMaxLeverageForMarket } from 'src/utils/position';
import { generateUserOrderIdForUser } from 'src/utils/order';
import {
	PublicKey,
	Transaction,
	TransactionInstruction,
	VersionedTransaction,
} from '@solana/web3.js';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useTxErrorHandler } from '../driftActions/transactions/useTxErrorHandler';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';
import invariant from 'tiny-invariant';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
import { useCurrentAccountPositions } from '../query/accountTradingData/usePositions';
import { useCurrentAccountOrders } from '../query/accountTradingData/useOrders';
import {
	batchInstructionsBySize,
	isTransactionSizeError,
} from 'src/utils/transaction';
import usePostHogCapture from '../posthog/usePostHogCapture';
import { FEE_PAYER_WALLET } from 'src/utils/wallets/feePayerWallet';

interface ClosePositionInstructionSet {
	marketIndex: number;
	instructions: TransactionInstruction[];
}

/**
 * Creates a build transaction function that uses the fee payer wallet.
 * This is used for size estimation during batching.
 */
function createBuildTransactionForSizing(
	driftClient: VelocityClient,
	feePayerPublicKey: PublicKey
) {
	return async (ixs: TransactionInstruction[]) => {
		const initialWallet = driftClient.txHandler.getWallet();
		try {
			driftClient.txHandler.updateWallet({
				publicKey: feePayerPublicKey,
				signTransaction: async (tx) => tx,
				signAllTransactions: async (txs) => txs,
			});
			const tx = await driftClient.buildTransaction(ixs, {
				useSimulatedComputeUnits: false, // Skip simulation for size checking
			});
			return tx;
		} finally {
			driftClient.txHandler.updateWallet(initialWallet);
		}
	};
}

/**
 * Hook to market close multiple positions at once.
 *
 * Settle pnl for the given markets, once the positions are closed. (handled in `useAutomatedTxnsListener`)
 *
 * Has the ability to create cancel orders for the given markets, once the positions are closed too. (handled in `useAutomatedTxnsListener`)
 *
 * Transactions are batched by size to avoid exceeding Solana's transaction size limits.
 * Batches are sent in parallel for faster execution.
 */
export default function useCloseMultiplePositions() {
	const currentUserAccount = useCurrentUserAccount();
	const driftClient = useDriftClient();
	const { captureEvent } = usePostHogCapture();

	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();
	const setDriftStore = useDriftStore((s) => s.set);

	const openPerpPositions = useCurrentAccountPositions();
	const openOrders = useCurrentAccountOrders();
	const openPerpPositionsRef = useRef(openPerpPositions);
	openPerpPositionsRef.current = openPerpPositions;
	const openOrdersRef = useRef(openOrders);
	openOrdersRef.current = openOrders;

	const closeMultiplePositions = useAutoSignDriftActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				marketIndexes: number[],
				cancelExistingOrders?: boolean
			) => {
				const userClient = currentUserAccount?.client;
				invariant(userClient, 'userClient is required');
				invariant(driftClient, 'driftClient is required');
				invariant(
					openPerpPositionsRef.current.length > 0,
					'openPerpPositions is required'
				);

				notify({
					id: toastId,
					type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
					message: isAutoSigned ? 'Closing positions...' : undefined,
					showUntilCancelled: true,
				});

				try {
					const settings = syncGetCurrentSettings();
					const isPlaceAndTake =
						driftClient.txVersion === 0 && !!settings?.placeAndTakeEnabled;

					// Get the initial user order ID before the loop
					const initialUserOrderId = generateUserOrderIdForUser(userClient);

					// Build close position instructions for each market separately
					const closePositionIxSets: ClosePositionInstructionSet[] = [];

					const positions = marketIndexes
						.map((marketIndex) => {
							const position = openPerpPositionsRef.current.find(
								(pos) => pos.marketIndex === marketIndex
							);
							if (!position) {
								console.error('Position not found for market', marketIndex);
								return undefined;
							}
							return position;
						})
						.filter(
							(position): position is OpenPosition => position !== undefined
						);

					// Build instructions for each position
					await Promise.all(
						positions.map(async (position, index) => {
							const marketIndex = position.marketIndex;

							const uiMarket = UIMarket.createPerpMarket(marketIndex);
							const { optionalAuctionParamsInputs } =
								ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps({
									leadSide: 'base',
									targetMarketIndex: marketIndex,
									currentSettings: settings,
									perpMarketAccount:
										driftClient.getPerpMarketAccountOrThrow(marketIndex),
									side: position.direction === 'long' ? 'sell' : 'buy',
									baseSizeStringValue: position.baseSize.abs().toString(),
									reduceOnly: true,
									orderType: 'market',
									slippageTolerance: settings?.slippageTolerance ?? 'dynamic',
								} as PerpTradeFormOutputProps & { leadSide: 'base' | 'quote' });

							const txParams = getTxParams({
								computeUnits: isPlaceAndTake ? MAX_COMPUTE_UNITS : undefined,
								marketId: MarketId.createPerpMarket(marketIndex),
								simulatedComputeUnitMultiplier: isPlaceAndTake
									? 1.4
									: undefined,
								lowerBoundCu: isPlaceAndTake
									? Env.placeAndTakeLowerBoundCu
									: undefined,
							});

							const positionMaxLeverage = getUserMaxLeverageForMarket(
								marketIndex,
								driftClient,
								userClient,
								uiMarket
							);

							const userOrderId = initialUserOrderId + index;

							const closePositionIxs =
								await simulatableCreatePerpMarketOrderIxs({
									driftClient,
									mainSignerOverride: signerWallet.publicKey,
									user: userClient,
									marketIndex,
									direction:
										position.direction === 'long'
											? PositionDirection.SHORT
											: PositionDirection.LONG,
									baseAmount: position.baseSize.abs(),
									optionalAuctionParamsInputs,
									placeAndTake: {
										enable: isPlaceAndTake,
										auctionDurationPercentage:
											Env.defaultAuctionDurationPercentage,
										referrerInfo: driftClient
											.getUserStatsOrThrow()
											.getReferrerInfo(),
									},
									txParams,
									positionMaxLeverage,
									userOrderId,
								});

							closePositionIxSets.push({
								marketIndex,
								instructions: Array.isArray(closePositionIxs)
									? closePositionIxs
									: [closePositionIxs],
							});
						})
					);

					if (closePositionIxSets.length === 0) {
						throw new Error('No close position instructions found');
					}

					// Build cancel orders and settle pnl instructions (kept as separate txs)
					let cancelOrdersIx: TransactionInstruction | undefined;
					if (cancelExistingOrders) {
						const existingOrderIdsToCancel = openOrdersRef.current
							.filter((order) => marketIndexes.includes(order.marketIndex))
							.map((order) => order.orderId);
						cancelOrdersIx = await createCancelOrdersIx({
							velocityClient: driftClient,
							user: userClient,
							orderIds: existingOrderIdsToCancel,
							mainSignerOverride: signerWallet.publicKey,
						});
					}

					const settlePnlIx = await createSettlePnlIx({
						velocityClient: driftClient,
						user: userClient,
						marketIndexes,
						mainSignerOverride: signerWallet.publicKey,
					});

					// Batch close position instructions by transaction size
					const buildTransactionForSizing = createBuildTransactionForSizing(
						driftClient,
						FEE_PAYER_WALLET.publicKey
					);

					const { batches, batchSizes } = await batchInstructionsBySize(
						closePositionIxSets,
						buildTransactionForSizing
					);

					// Log batching telemetry
					captureEvent('trade_close_multiple_positions_batched', {
						total_positions: closePositionIxSets.length,
						batch_count: batches.length,
						batch_sizes: batchSizes,
						is_place_and_take: isPlaceAndTake,
						market_indexes: marketIndexes,
					});

					const txParams = getTxParams({
						computeUnits: isPlaceAndTake ? MAX_COMPUTE_UNITS : undefined,
						simulatedComputeUnitMultiplier: isPlaceAndTake ? 1.4 : undefined,
						lowerBoundCu: isPlaceAndTake
							? Env.placeAndTakeLowerBoundCu
							: undefined,
					});

					// Build and sign all batches plus settle/cancel txs
					const ixsToSign: Record<string, TransactionInstruction[]> = {};

					// Add close position batches
					batches.forEach((batch, index) => {
						const batchIxs = batch.flatMap((set) => set.instructions);
						ixsToSign[`closePositions_${index}`] = batchIxs;
					});

					// Add settle and cancel as separate txs
					if (settlePnlIx) {
						ixsToSign['settlePnl'] = [settlePnlIx];
					}
					if (cancelOrdersIx) {
						ixsToSign['cancelOrders'] = [cancelOrdersIx];
					}

					const { signedTxMap } =
						await signerWallet.buildAndSignTransactionsMap(ixsToSign, txParams);

					// Send all close position batches in parallel
					const closePositionTxKeys = Object.keys(signedTxMap).filter((key) =>
						key.startsWith('closePositions_')
					);

					const sendResults = await Promise.allSettled(
						closePositionTxKeys.map(async (key) => {
							const tx = signedTxMap[key] as VersionedTransaction;
							if (!tx) return null;

							const { txSig } = await driftClient.sendTransaction(
								tx,
								undefined,
								undefined,
								true
							);
							return { key, txSig };
						})
					);

					// Collect successful tx signatures
					const successfulTxSigs: string[] = [];
					const failedBatches: string[] = [];

					for (const result of sendResults) {
						if (result.status === 'fulfilled' && result.value?.txSig) {
							successfulTxSigs.push(result.value.txSig);
						} else if (result.status === 'rejected') {
							const error = result.reason;
							console.error('Failed to send close position batch:', error);

							// Check if it's a size error
							if (isTransactionSizeError(error)) {
								failedBatches.push('size_error');
							} else {
								failedBatches.push(String(error));
							}
						}
					}

					// Queue settle pnl for auto-execution
					if (signedTxMap.settlePnl) {
						setDriftStore((s) => {
							s.pnlsAwaitingAutoSettle = [
								...s.pnlsAwaitingAutoSettle,
								{
									marketIndexesOfPnls: marketIndexes,
									signedSettlePnlsTx: signedTxMap.settlePnl as
										| Transaction
										| VersionedTransaction,
								},
							];
						});
					}

					// Queue cancel orders for auto-execution
					if (signedTxMap.cancelOrders) {
						setDriftStore((s) => {
							s.ordersAwaitingAutoCancel = [
								...s.ordersAwaitingAutoCancel,
								{
									marketIndexesOfOrders: marketIndexes,
									signedCancelExistingOrdersTx: signedTxMap.cancelOrders as
										| Transaction
										| VersionedTransaction,
								},
							];
						});
					}

					// Notify based on results
					if (successfulTxSigs.length > 0) {
						const message =
							batches.length > 1
								? `Market orders placed in (${successfulTxSigs.length}/${batches.length} transactions).`
								: 'Market orders placed.';

						notify({
							type: failedBatches.length > 0 ? 'warning' : 'success',
							message,
							lengthMs: 5000,
							id: toastId,
							updatePrevious: true,
						});
					} else if (failedBatches.length > 0) {
						const hasSizeError = failedBatches.includes('size_error');
						notify({
							type: 'error',
							message: hasSizeError
								? 'Transaction too large. Please try closing fewer positions at once.'
								: 'Failed to close positions.',
							lengthMs: 5000,
							id: toastId,
							updatePrevious: true,
						});
					}

					// Return the first successful tx signature (for backwards compatibility)
					return successfulTxSigs[0] ?? undefined;
				} catch (error) {
					console.error('Error closing multiple positions', error);

					// Check if it's a transaction size error
					if (isTransactionSizeError(error)) {
						notify({
							type: 'error',
							message:
								'Transaction too large. Please try closing fewer positions at once.',
							lengthMs: 5000,
							id: toastId,
							updatePrevious: true,
						});

						captureEvent('trade_close_multiple_positions_size_error', {
							market_indexes: marketIndexes,
							error_message:
								error instanceof Error ? error.message : String(error),
						});

						return undefined;
					}

					if (error instanceof FeePayerError) {
						throw error;
					}

					handleTxError(error, toastId);

					return undefined;
				}
			},
			[
				currentUserAccount,
				driftClient,
				getTxParams,
				handleTxError,
				setDriftStore,
			]
		)
	);

	return closeMultiplePositions;
}
